import React, {createContext, useCallback, useContext, useEffect, useRef, useState} from 'react';

export type GalleryImage = {
	id: string;
	url: string;
	preview: string;
	description: string;
	credits?: string | null;
	width?: number | null;
	height?: number | null;
};

type GalleryContextType = {
	images: GalleryImage[];
	openId: string | undefined;
	registerImages: (images: GalleryImage[]) => void;
	unregisterImages: (ids: string[]) => void;
	openImage: (id: string) => void;
	navigateTo: (id: string) => void;
	close: () => void;
};

export const GalleryContext = createContext<GalleryContextType | null>(null);

const HASH_PREFIX = 'gallery=';

function getHashImageId(): string | undefined {
	if (typeof window === 'undefined') return undefined;
	const hash = window.location.hash.slice(1);
	return hash.startsWith(HASH_PREFIX) ? hash.slice(HASH_PREFIX.length) : undefined;
}

export const GalleryProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
	const [images, setImages] = useState<GalleryImage[]>([]);
	// на сервере адресной строки нет, поэтому якорь читаем уже после гидрации: иначе первый
	// рендер на клиенте отличается от серверного и React перерисовывает страницу целиком
	const [openId, setOpenId] = useState<string | undefined>(undefined);

	const registerImages = useCallback((newImages: GalleryImage[]) => {
		setImages((prev) => {
			const ids = new Set(newImages.map((i) => i.id));
			return [...prev.filter((i) => !ids.has(i.id)), ...newImages];
		});
	}, []);

	const unregisterImages = useCallback((ids: string[]) => {
		const idSet = new Set(ids);
		setImages((prev) => prev.filter((i) => !idSet.has(i.id)));
	}, []);

	const openImage = useCallback((id: string) => {
		window.history.pushState({fromGallery: true}, '', `#${HASH_PREFIX}${id}`);
		setOpenId(id);
	}, []);

	const navigateTo = useCallback((id: string) => {
		window.history.replaceState(window.history.state, '', `#${HASH_PREFIX}${id}`);
		setOpenId(id);
	}, []);

	const close = useCallback(() => {
		if ((window.history.state as any)?.fromGallery) {
			window.history.back();
		} else {
			window.history.replaceState(null, '', window.location.pathname + window.location.search);
			setOpenId(undefined);
		}
	}, []);

	useEffect(() => {
		setOpenId(getHashImageId());
		const handlePopState = () => setOpenId(getHashImageId());
		window.addEventListener('popstate', handlePopState);
		return () => window.removeEventListener('popstate', handlePopState);
	}, []);

	return (
		<GalleryContext.Provider value={{images, openId, registerImages, unregisterImages, openImage, navigateTo, close}}>
			{children}
		</GalleryContext.Provider>
	);
};

export function useGalleryContext() {
	return useContext(GalleryContext);
}

export function useGallery(): (id: string) => void {
	const ctx = useContext(GalleryContext);
	if (!ctx) throw new Error('useGallery must be used within GalleryProvider');
	return ctx.openImage;
}

/** Регистрирует изображения в галерею без их отображения. */
export function useGalleryImages(images: GalleryImage[]) {
	const ctx = useContext(GalleryContext);
	const {registerImages, unregisterImages} = ctx ?? {};
	const registeredIdsRef = useRef<string[]>([]);

	useEffect(() => {
		if (!registerImages || !unregisterImages) return;
		if (registeredIdsRef.current.length) unregisterImages(registeredIdsRef.current);
		registerImages(images);
		registeredIdsRef.current = images.map((i) => i.id);
		return () => {
			unregisterImages(registeredIdsRef.current);
			registeredIdsRef.current = [];
		};
	}, [images]);
}
